import React, { ReactElement, useState } from "react";
import { Control, Controller, FormState, UseFormWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { domainValidation } from "constants/regexp";
import { wordpressPhpVersionLimiter } from "constants/webApp";

import IconSelector from "components/shared/images/IconSelector";

import { getDockerProxy } from "redux/handlers/dockerHandler";
import { generatePassword } from "redux/handlers/userHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { IDockerTypeVersions, IWebAppEnviromentVersion } from "types/api/dockerApiInterface";
import { IServerByTypeAPI } from "types/api/serversApiInterface";

type controlType = {
 server: number;
 site_name: string;
 wp_version: string;
 app_type: string;
 app_version: string;
 db_name: string;
 db_type: string;
 db_version: string;
 db_user: string;
 db_password: string;
};

const WordpressWebAppContent = ({
 control,
 formState,
 servers,
 cms,
 appVers,
 dbTypes,
 dbVersions,
 watch,
 setValue,
 handleChangeDatabase,
 handleOpen,
 handleReset
}: {
 control: Control<controlType>;
 formState: FormState<controlType>;
 servers: Array<IServerByTypeAPI>;
 cms: IWebAppEnviromentVersion | null;
 appVers: IDockerTypeVersions | null;
 dbTypes: Array<{ id: number; name: string }>;
 dbVersions: Array<{ id: number; name: string }>;
 watch: UseFormWatch<controlType>;
 setValue: (type: keyof controlType, value: string | number) => void;
 handleChangeDatabase: (value: string) => void;
 handleOpen: () => void;
 handleReset: () => void;
}): ReactElement => {
 const dispatch = useAppDispatch();
 const intl = useIntl();

 const [proxyNotFound, setProxyNotFound] = useState<boolean>(false);
 const [showPassword, setShowPassword] = useState<string>("password");

 const handleChangeServer = async (id: number) => {
  setProxyNotFound(false);
  const containerList = await dispatch(getDockerProxy(id, 0, 10));
  if (
   containerList.totalCount === 0 ||
   !containerList.dataset.find((element) => element.name === "nginx_certbot")
  )
   setProxyNotFound(true);
  setValue("server", id);
 };

 const handleGeneratePassword = async () => {
  setValue("db_password", await generatePassword());
 };

 const checkDisableConfirm = () => {
  return (
   watch("server") === 0 ||
   proxyNotFound ||
   watch("site_name") === "" ||
   watch("wp_version") === "" ||
   !domainValidation.test(watch("site_name")) ||
   watch("app_version") === "" ||
   watch("db_name") === "" ||
   watch("db_type") === "" ||
   watch("db_version") === "" ||
   watch("db_user") === "" ||
   watch("db_password") === ""
  );
 };

 return (
  <Stack spacing={2}>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.selectServer" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.selectServerMessage" />
      </Typography>
      <Controller
       name="server"
       control={control}
       render={({ field }) => (
        <Autocomplete
         fullWidth={true}
         autoHighlight
         sx={{ my: 2 }}
         onChange={(e, value) => {
          handleChangeServer(value?.id || 0);
         }}
         options={servers.map((server) => {
          return { label: server.server_name, id: server.id };
         })}
         value={servers
          .map((element) => {
           return { label: element.server_name, id: element.id };
          })
          .find((server) => server.id === watch("server"))}
         renderInput={(params) => (
          <TextField
           {...params}
           {...field}
           label={<FormattedMessage id="docker.website.add.serverHost" />}
           error={formState.isDirty && !!formState?.errors?.server}
           InputLabelProps={{ shrink: true }}
          />
         )}
        />
       )}
      />
      {proxyNotFound && (
       <Alert severity="error">
        <Typography>
         <FormattedMessage id="docker.website.noProxyFound" />
        </Typography>
       </Alert>
      )}
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.siteName" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.siteNameMessage" />
      </Typography>
      <Controller
       name="site_name"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         value={watch("site_name")}
         onChange={({ currentTarget }) => setValue("site_name", currentTarget.value.toLowerCase())}
         label={intl.formatMessage({ id: "docker.website.add.siteName" })}
         error={formState.isDirty && !!formState?.errors?.site_name}
         InputLabelProps={{ shrink: true }}
         sx={{ my: 2 }}
         autoComplete="off"
         helperText={
          formState.isDirty &&
          !!formState?.errors?.site_name &&
          intl.formatMessage({ id: "login.passwordMinLength" })
         }
        />
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.wordpressVersion" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.wordpressVersionMessage" />
      </Typography>
      <Controller
       name="wp_version"
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <FormControl fullWidth>
         <InputLabel>
          <FormattedMessage id="docker.website.wordpressVersion" />
         </InputLabel>
         <Select
          {...field}
          value={watch("wp_version")}
          label={<FormattedMessage id="docker.website.wordpressVersion" />}
          onChange={(e) => setValue("wp_version", e.target.value)}>
          {cms?.versions
           .filter((cms) => cms.enviroment === "wordpress")
           .map((element, index) => {
            return (
             <MenuItem value={element.version} key={`wp-vers-${index}`}>
              {element.version}
             </MenuItem>
            );
           })}
         </Select>
        </FormControl>
       )}
      />
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.appVersion" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.appVersionMessage" />
      </Typography>
      {appVers ? (
       <Controller
        name="app_version"
        control={control}
        render={({ field }) => (
         <Autocomplete
          fullWidth={true}
          autoHighlight
          sx={{ my: 2 }}
          onChange={(e, value) => {
           setValue("app_version", value?.label || "");
          }}
          options={appVers?.versions
           .filter(
            (element) =>
             element.name.includes(watch("app_type")) &&
             !element.name.includes("litespeed") &&
             !(
              wordpressPhpVersionLimiter.find((wpvers) => wpvers.version === watch("wp_version"))
               ?.ph_not_wanted || ""
             ).includes(element.name)
           )
           .map((app) => {
            return { label: app.name, id: app.id };
           })}
          value={appVers?.versions
           .map((element) => {
            return { label: element.name, id: element.id };
           })
           .find((app) => app.label === watch("app_version"))}
          renderInput={(params) => (
           <TextField
            {...params}
            {...field}
            label={<FormattedMessage id="docker.website.add.appVersion" />}
            error={formState.isDirty && !!formState?.errors?.app_version}
            InputLabelProps={{ shrink: true }}
           />
          )}
         />
        )}
       />
      ) : (
       <CircularProgress />
      )}
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.databaseData" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.databaseDataMessage" />
      </Typography>
      <Stack spacing={2}>
       <Controller
        name="db_name"
        control={control}
        rules={{
         required: true
        }}
        render={({ field }) => (
         <TextField
          {...field}
          fullWidth={true}
          label={intl.formatMessage({ id: "docker.website.databaseName" })}
          error={formState.isDirty && !!formState?.errors?.db_name}
          InputLabelProps={{ shrink: true }}
          sx={{ my: 2 }}
          autoComplete="off"
          helperText={
           formState.isDirty &&
           !!formState?.errors?.db_name &&
           intl.formatMessage({ id: "login.passwordMinLength" })
          }
         />
        )}
       />
       <Controller
        name="db_type"
        control={control}
        rules={{
         required: true
        }}
        render={({ field }) => (
         <FormControl fullWidth>
          <InputLabel>
           <FormattedMessage id="docker.website.databaseType" />
          </InputLabel>
          <Select
           {...field}
           value={watch("db_type")}
           label={<FormattedMessage id="docker.website.databaseType" />}
           onChange={(e) => handleChangeDatabase(e.target.value)}>
           {dbTypes
            .filter((db) => db.name !== "postgres")
            .map((element, index) => {
             return (
              <MenuItem value={element.name} key={`db-type-${index}`}>
               {element.name}
              </MenuItem>
             );
            })}
          </Select>
         </FormControl>
        )}
       />
       <Controller
        name="db_version"
        control={control}
        rules={{
         required: true
        }}
        render={({ field }) => (
         <FormControl fullWidth>
          <InputLabel>
           <FormattedMessage id="docker.website.databaseVersion" />
          </InputLabel>
          <Select
           {...field}
           disabled={watch("db_type") === ""}
           value={watch("db_version")}
           label={<FormattedMessage id="docker.website.databaseVersion" />}
           onChange={(e) => setValue("db_version", e.target.value)}>
           {dbVersions.map((element, index) => {
            return (
             <MenuItem value={element.name} key={`db-vers-${index}`}>
              {element.name}
             </MenuItem>
            );
           })}
          </Select>
         </FormControl>
        )}
       />
      </Stack>
     </Stack>
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, textAlign: "left" }}>
    <Typography variant="unboundedTitle" fontSize={18}>
     <FormattedMessage id="docker.website.add.databaseUser" />
    </Typography>
    <Stack mt={2}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="docker.website.add.databaseUserMessage" />
      </Typography>
      <Stack>
       <Controller
        name="db_user"
        control={control}
        rules={{
         required: true
        }}
        render={({ field }) => (
         <TextField
          {...field}
          fullWidth={true}
          value={watch("db_user")}
          label={intl.formatMessage({ id: "docker.website.databaseUser" })}
          error={formState.isDirty && !!formState?.errors?.db_user}
          InputLabelProps={{ shrink: true }}
          sx={{ my: 2 }}
          autoComplete="off"
          helperText={
           formState.isDirty &&
           !!formState?.errors?.db_user &&
           intl.formatMessage({ id: "login.passwordMinLength" })
          }
         />
        )}
       />
       <Stack direction="row" alignItems="center" spacing={2}>
        <Controller
         name="db_password"
         control={control}
         rules={{
          required: true
         }}
         render={({ field }) => (
          <TextField
           {...field}
           fullWidth={true}
           label={intl.formatMessage({ id: "docker.website.databasePassword" })}
           error={formState.isDirty && !!formState?.errors?.db_password}
           InputLabelProps={{ shrink: true }}
           sx={{ my: 2 }}
           autoComplete="new-password"
           type={showPassword}
           helperText={
            formState.isDirty &&
            !!formState?.errors?.db_password &&
            intl.formatMessage({ id: "login.passwordMinLength" })
           }
           InputProps={{
            endAdornment: (
             <InputAdornment position="end">
              <IconButton
               aria-label="toggle password visibility"
               onClick={() => setShowPassword(showPassword === "password" ? "text" : "password")}
               edge="end">
               <IconSelector
                icon={showPassword === "text" ? "VisibilityOffIcon" : "VisibilityIcon"}
                props={{ fontSize: "small" }}
               />
              </IconButton>
             </InputAdornment>
            )
           }}
          />
         )}
        />
        <Button onClick={handleGeneratePassword} sx={{ width: "40%" }}>
         <FormattedMessage id="app.generatePassword" />
        </Button>
       </Stack>
      </Stack>
     </Stack>
     <Stack direction="row" justifyContent="flex-end" alignItems="center">
      <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
       <FormattedMessage id="app.wizard.reset" />
      </Button>
      <Button sx={{ mt: 1, mr: 1 }} disabled={checkDisableConfirm()} onClick={handleOpen}>
       <FormattedMessage id="app.wizard.confirm" />
      </Button>
     </Stack>
    </Stack>
   </Paper>
  </Stack>
 );
};

export default WordpressWebAppContent;
